import { customerViewExpiresInMs, isCustomerViewActive } from './customerViewExpiry';
import { readCustomerViewStartedAtMs, subscribeToCustomerView } from './customerViewStorage';

type Watcher = { userUuid: string | undefined; listener: () => void };

const watchers = new Set<Watcher>();
let expiryTimer: ReturnType<typeof setTimeout> | undefined;
let stopWatchingStorage: (() => void) | undefined;

export const readIsCustomerViewOn = (userUuid: string | undefined): boolean =>
  userUuid !== undefined && isCustomerViewActive(readCustomerViewStartedAtMs(userUuid), Date.now());

const soonestExpiryInMs = (): number | undefined => {
  const nowMs = Date.now();
  const pendingExpiries = [...watchers].flatMap(({ userUuid }) => {
    const startedAtMs = userUuid === undefined ? null : readCustomerViewStartedAtMs(userUuid);
    if (startedAtMs === null) return [];
    const expiresInMs = customerViewExpiresInMs(startedAtMs, nowMs);
    return expiresInMs > 0 ? [expiresInMs] : [];
  });
  return pendingExpiries.length > 0 ? Math.min(...pendingExpiries) : undefined;
};

const armExpiryTimer = () => {
  clearTimeout(expiryTimer);
  const expiresInMs = soonestExpiryInMs();
  expiryTimer = expiresInMs === undefined ? undefined : setTimeout(recheckAllWatchers, expiresInMs);
};

function recheckAllWatchers() {
  watchers.forEach(({ listener }) => listener());
  armExpiryTimer();
}

const startWatching = () => {
  document.addEventListener('visibilitychange', recheckAllWatchers);
  window.addEventListener('focus', recheckAllWatchers);
  stopWatchingStorage = subscribeToCustomerView(recheckAllWatchers);
};

const stopWatching = () => {
  document.removeEventListener('visibilitychange', recheckAllWatchers);
  window.removeEventListener('focus', recheckAllWatchers);
  stopWatchingStorage?.();
  stopWatchingStorage = undefined;
  clearTimeout(expiryTimer);
  expiryTimer = undefined;
};

export const subscribeToCustomerViewActivity = (
  userUuid: string | undefined,
  listener: () => void,
) => {
  const watcher: Watcher = { userUuid, listener };
  if (watchers.size === 0) startWatching();
  watchers.add(watcher);
  armExpiryTimer();
  return () => {
    watchers.delete(watcher);
    if (watchers.size === 0) {
      stopWatching();
    } else {
      armExpiryTimer();
    }
  };
};
